/**
 * The wake: a small fluid the sub stirs as it moves.
 *
 * A GPU Navier-Stokes at an eighth of the screen, in WORLD space. Advect the
 * velocity by itself, inject a splat behind the propeller, project the result
 * to be divergence-free, and let the composite refract the scene through what
 * comes out. The projection is the whole point — without it the dye blooms
 * outward like smoke, and with it a push has to go somewhere and goes AROUND,
 * which is the curl that reads as water rather than as a particle effect.
 *
 * It is deliberately cheap. At 1/8 scale the grid is a few tens of thousands
 * of texels, so twenty Jacobi sweeps cost less than one full-screen pass of
 * anything else in the chain. The expensive part of a fluid is the resolution
 * nobody can see.
 */

import fluidCode from './shaders/fluid.wgsl?raw';
import { checkShader, makeUniform } from './gpu';

/** Pressure sweeps. Past about sixteen the curl stops visibly improving. */
const JACOBI = 16;

export interface WakeInput {
  /** World position of the wash, and the world velocity being pushed into it. */
  x: number; y: number;
  vx: number; vy: number;
  /** 0..1 — how hard the drive is working. Nothing is injected at 0. */
  thrust: number;
  /** Seconds. */
  dt: number;
  /** The world rect the field covers, centred on the camera. */
  camX: number; camY: number;
  halfW: number; halfH: number;
}

export class Fluid {
  readonly view: GPUTextureView;

  private device: GPUDevice;
  private w: number;
  private h: number;
  private field: GPUTexture[] = [];
  private fieldView: GPUTextureView[] = [];
  private press: GPUTexture[] = [];
  private pressView: GPUTextureView[] = [];
  private divTex!: GPUTexture;
  private divView!: GPUTextureView;

  private uni: GPUBuffer;
  private scratch = new Float32Array(20);
  private layout: GPUBindGroupLayout;
  private advect!: GPURenderPipeline;
  private diverge!: GPURenderPipeline;
  private jacobi!: GPURenderPipeline;
  private gradient!: GPURenderPipeline;
  private samp: GPUSampler;
  /** Bind groups are (src, aux) pairs; every combination used is made once. */
  private binds = new Map<string, GPUBindGroup>();

  /** Last frame's world window, so the field can be held still in world space. */
  private lastX = 0;
  private lastY = 0;
  private primed = false;

  constructor(device: GPUDevice, width: number, height: number) {
    this.device = device;
    // A fifth, not an eighth. The eighth was chosen for cost and it is still
    // nearly free at a fifth — 16 sweeps over ~83k texels at 1080p — but the
    // eddies at an eighth were too coarse to read as anything but a blur.
    // Resolution is where a fluid's DETAIL comes from; it is only the full-res
    // passes that are expensive, and none of these are.
    this.w = Math.max(48, Math.round(width / 5));
    this.h = Math.max(48, Math.round(height / 5));
    this.samp = device.createSampler({
      magFilter: 'linear', minFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
    });
    this.uni = makeUniform(device, 80, 'fluid');

    const make = (fmt: GPUTextureFormat, label: string) => device.createTexture({
      size: [this.w, this.h], format: fmt, label,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING,
    });
    for (const k of [0, 1]) {
      this.field.push(make('rgba16float', `wake-field${k}`));
      this.press.push(make('r16float', `wake-press${k}`));
    }
    this.fieldView = this.field.map((t) => t.createView());
    this.pressView = this.press.map((t) => t.createView());
    this.divTex = make('r16float', 'wake-div');
    this.divView = this.divTex.createView();
    this.view = this.fieldView[0]!;

    const mod = device.createShaderModule({ code: fluidCode, label: 'fluid' });
    checkShader(mod, 'fluid', fluidCode);
    this.layout = device.createBindGroupLayout({
      label: 'fluid',
      entries: [
        { binding: 0, visibility: GPUShaderStage.FRAGMENT, sampler: {} },
        { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: {} },
        { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: {} },
        { binding: 3, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      ],
    });
    const pl = device.createPipelineLayout({ bindGroupLayouts: [this.layout] });
    const pipe = (entry: string, fmt: GPUTextureFormat) => device.createRenderPipeline({
      label: `fluid:${entry}`,
      layout: pl,
      vertex: { module: mod, entryPoint: 'vsFull' },
      fragment: { module: mod, entryPoint: entry, targets: [{ format: fmt }] },
      primitive: { topology: 'triangle-list' },
    });
    this.advect = pipe('fsAdvect', 'rgba16float');
    this.diverge = pipe('fsDivergence', 'r16float');
    this.jacobi = pipe('fsJacobi', 'r16float');
    this.gradient = pipe('fsGradient', 'rgba16float');
  }

  /** Cached, because a pipeline change is cheap and a bind group churn is not. */
  private bind(src: GPUTextureView, aux: GPUTextureView, key: string): GPUBindGroup {
    let b = this.binds.get(key);
    if (!b) {
      b = this.device.createBindGroup({
        layout: this.layout,
        entries: [
          { binding: 0, resource: this.samp },
          { binding: 1, resource: src },
          { binding: 2, resource: aux },
          { binding: 3, resource: { buffer: this.uni } },
        ],
      });
      this.binds.set(key, b);
    }
    return b;
  }

  /**
   * The finished field, and it is ALWAYS slot 0.
   *
   * There is no ping-pong to track: advection writes the scratch slot and the
   * gradient pass writes the result back to 0, so a bind group made once at
   * startup stays correct forever. Worth stating, because a field that looked
   * like it alternated would have to be rebound every frame.
   */
  get current(): GPUTextureView {
    return this.fieldView[0]!;
  }

  /**
   * Record a whole simulation step into `enc`.
   *
   * Returns nothing and owns its own ping-pong: the caller hands over the
   * boat's state and reads `current` afterwards.
   */
  step(enc: GPUCommandEncoder, p: WakeInput) {
    const spanX = p.halfW * 2, spanY = p.halfH * 2;
    if (!this.primed) { this.lastX = p.camX; this.lastY = p.camY; this.primed = true; }
    // How far the world window moved, in UV. Positive x drift means the window
    // slid right, so what was here is now to the LEFT — hence the sign.
    const driftX = (p.camX - this.lastX) / Math.max(1e-3, spanX);
    const driftY = -(p.camY - this.lastY) / Math.max(1e-3, spanY);
    this.lastX = p.camX;
    this.lastY = p.camY;

    // The wash sits behind the boat and pushes the way the boat is going.
    const uvX = (p.x - p.camX) / Math.max(1e-3, spanX) + 0.5;
    const uvY = 0.5 - (p.y - p.camY) / Math.max(1e-3, spanY);
    const push = Math.min(1, p.thrust);
    const s = this.scratch;
    s[0] = 1 / this.w; s[1] = 1 / this.h; s[2] = Math.min(0.05, p.dt); s[3] = 0;
    s[4] = uvX; s[5] = uvY;
    // World velocity into UV per second, negated in y for the flipped axis.
    s[6] = (p.vx / Math.max(1e-3, spanX)) * push * 2.2;
    s[7] = -(p.vy / Math.max(1e-3, spanY)) * push * 2.2;
    s[8] = 0.026;                 // splat radius, UV — a cord off the screw, not a cloud
    s[9] = push * 5.2 * s[2];     // dye — denser, because the splat is much smaller now
    s[10] = 0.985;                // velocity decay
    s[11] = 0.994;                // dye decay — a longer trail
    s[12] = driftX; s[13] = driftY; s[14] = 0; s[15] = 0;
    s[16] = spanX; s[17] = spanY; s[18] = 0; s[19] = 0;
    this.device.queue.writeBuffer(this.uni, 0, s);

    /**
     * One pass, with the inputs and the target named together.
     *
     * They are named together ON PURPOSE. Binding a texture as an input while
     * it is also the render target is invalid in WebGPU, and it does not fail
     * where you wrote it — the whole command buffer is rejected at submit, so
     * every pass in the frame vanishes and the screen goes black with nothing
     * to say which one did it. Passing the bind group in ready-made hid that
     * relationship; passing the three views makes it checkable, and checked.
     */
    const pass = (
      pipe: GPURenderPipeline, src: GPUTextureView, aux: GPUTextureView,
      target: GPUTextureView, key: string, label: string,
    ) => {
      if (src === target || aux === target) {
        console.error(`[abyss] fluid ${label}: a bound texture is also the target`);
        return;
      }
      const rp = enc.beginRenderPass({
        label,
        colorAttachments: [{
          view: target, clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: 'clear', storeOp: 'store',
        }],
      });
      rp.setPipeline(pipe);
      rp.setBindGroup(0, this.bind(src, aux, key));
      rp.draw(3);
      rp.end();
    };

    const a = 0, b = 1;   // result, scratch
    // 1. advect the field by itself, and inject the wash.
    pass(this.advect, this.fieldView[a]!, this.pressView[0]!, this.fieldView[b]!, `adv${a}`, 'wake:advect');
    // 2. how much that field is compressing.
    // `aux` is unused by this entry point but the layout still demands a
    // texture, and it must not be the target — which is exactly what it was.
    pass(this.diverge, this.fieldView[b]!, this.fieldView[a]!, this.divView, `div${b}`, 'wake:div');
    // 3. solve for the pressure that cancels it.
    let pa = 0;
    for (let k = 0; k < JACOBI; k++) {
      const pb = 1 - pa;
      pass(this.jacobi, this.pressView[pa]!, this.divView, this.pressView[pb]!, `jac${pa}`, 'wake:jacobi');
      pa = pb;
    }
    // 4. subtract its gradient — the step that turns a push into a swirl.
    pass(this.gradient, this.fieldView[b]!, this.pressView[pa]!, this.fieldView[a]!, `grad${b}:${pa}`, 'wake:grad');
    // `a` holds the finished field; the next frame advects from it.
  }
}
